/** @file Named entity list: select, create, rename (double-click or button) and delete, all by callback. */
import type { Translate } from '@pose2d/i18n/types';
import { useState } from 'react';
import { Button } from '../../ui-kit/Button';
import { joinClass } from '../../ui-kit/logic/joinClass';
import { TextInput } from '../../ui-kit/TextInput';

/** One row: `name` is entity data (never translated); `note` is shown after it (already translated or data). */
export interface NamedRow {
  id: string;
  name: string;
  note?: string;
}

/** Props of NamedList; a missing callback hides its action. */
export interface NamedListProps {
  t: Translate;
  /** Already-translated accessible name of the list. */
  label: string;
  rows: readonly NamedRow[];
  selected: string | null;
  onSelect: (id: string) => void;
  onCreate?: (() => void) | undefined;
  onRename?: ((id: string, name: string) => void) | undefined;
  onDelete?: ((id: string) => void) | undefined;
  /** Ids whose Delete is disabled up front (the default skin). */
  locked?: ReadonlySet<string>;
}

interface RowProps {
  p: NamedListProps;
  row: NamedRow;
  renaming: boolean;
  setRenaming: (id: string | null) => void;
}

function Row({ p, row, renaming, setRenaming }: RowProps) {
  const commit = (name: string): void => { setRenaming(null); p.onRename?.(row.id, name); };
  return (
    <li className={joinClass('rig-list-row', row.id === p.selected && 'rig-list-row-selected')}>
      {renaming
        ? <TextInput value={row.name} label={p.t('panels.rig.rename')} autoFocus onCommit={commit} onCancel={() => { setRenaming(null); }} />
        : <button type="button" aria-pressed={row.id === p.selected} onClick={() => { p.onSelect(row.id); }} onDoubleClick={() => { if (p.onRename) setRenaming(row.id); }}>{row.name}</button>}
      {row.note !== undefined && <span className="rig-note">{row.note}</span>}
    </li>
  );
}

function Actions({ p, setRenaming }: Pick<RowProps, 'p' | 'setRenaming'>) {
  const sel = p.selected;
  const locked = sel !== null && (p.locked?.has(sel) ?? false);
  return (
    <div className="rig-actions">
      {p.onCreate && <Button label={p.t('panels.rig.create')} onClick={p.onCreate} />}
      {p.onRename && <Button label={p.t('panels.rig.rename')} disabled={sel === null} onClick={() => { setRenaming(sel); }} />}
      {p.onDelete && <Button label={p.t('panels.rig.delete')} danger disabled={sel === null || locked} onClick={() => { if (sel !== null) p.onDelete?.(sel); }} />}
    </div>
  );
}

/** Rows are plain buttons (Tab/Enter/Space work natively, no key handlers); rename edits the row in place. */
export function NamedList(p: NamedListProps) {
  const [renaming, setRenaming] = useState<string | null>(null);
  return (
    <div className="rig-list">
      <ul className="rig-list-rows" aria-label={p.label}>
        {p.rows.map((row) => <Row key={row.id} p={p} row={row} renaming={row.id === renaming} setRenaming={setRenaming} />)}
      </ul>
      <Actions p={p} setRenaming={setRenaming} />
    </div>
  );
}
